import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, displayName } from '@promanage/contracts/person-display';
import {
  SUPPLIER_TRADES,
  SUPPLIER_TRADE_LABELS,
  formatRef,
  parseRef,
  requiresRegistration,
  type SupplierTrade,
} from '@promanage/core';
import {
  Button,
  Chip,
  Field,
  Input,
  Panel,
  PanelHeader,
  Ref,
  Select,
  Textarea,
} from '@promanage/ui';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { RecordActions } from '../components/RecordActions';
import { RelatedRecordRow } from '../components/RelatedRecordRow';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { RecordIdentity } from '../components/RecordIdentity';
import { Icon } from '../components/Icon';
import { AttachRoleDialog } from '../features/people/AttachRole';
import { SupplierAccount } from '../features/suppliers/SupplierAccount';
import { contactMethods } from '../features/people/detail';
import type { SupplierDetail } from '../features/suppliers/types';
import { trpc } from '../lib/trpc';
import { SavedNote, useUnsavedChanges } from '../components/UnsavedChanges';

/**
 * One contractor, addressed by its display ref: /maintenance/contractors/SU000003.
 *
 * Same ref contract as /owners/$ref — `beforeLoad` normalises anything ref-shaped to the canonical
 * form, and the component answers each remaining wrong shape distinctly, because a ref for another
 * entity, a typo, and a number this agency never issued are three different mistakes.
 *
 * THREE PANELS, IN THE ORDER THE WORK HAPPENS: who they are, who to ring, and where they are paid.
 * The last one is the one with the controls on it, and it is deliberately last rather than first —
 * a page that opens on a bank account is a page people learn to scroll past the top of.
 */
export const Route = createFileRoute('/maintenance/contractors/$ref')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'supplier') {
      const canonical = formatRef('supplier', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({
          to: '/maintenance/contractors/$ref',
          params: { ref: canonical },
          replace: true,
        });
      }
    }
  },
  component: SupplierDetailPage,
});

function SupplierDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  const record = useQuery(
    trpc.suppliers.get.queryOptions(
      { refSeq: parsed?.sequence ?? 0 },
      { enabled: parsed?.entity === 'supplier', staleTime: 10_000 },
    ),
  );

  if (parsed === null || parsed.entity !== 'supplier') {
    return (
      <NotFound
        title={parsed === null ? `“${ref}” is not a record reference` : 'That is not a contractor'}
      >
        {parsed === null
          ? 'A contractor reference looks like SU000003.'
          : `${ref} belongs to another kind of record. Contractor references start with SU.`}
      </NotFound>
    );
  }

  if (record.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Contractor" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  if (record.error || !record.data) {
    return (
      <NotFound title="No such contractor">
        {`${ref} is not a contractor in this agency. It may have been keyed from another agency's records.`}
      </NotFound>
    );
  }

  return <SupplierRecord supplier={record.data} />;
}

function SupplierRecord({ supplier }: { supplier: SupplierDetail }) {
  const form = useUnsavedChanges();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.suppliers.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.suppliers.list.queryKey() });
  };

  const setStatus = useMutation(
    trpc.suppliers.setStatus.mutationOptions({
      meta: { errorTitle: 'Could not change that contractor’s status' },
      onSuccess: refresh,
    }),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={supplier.name}
        subtitle={SUPPLIER_TRADE_LABELS[supplier.trade]}
        actions={
          <>
            <Link to="/maintenance/contractors">
              <Button size="sm" variant="secondary">
                All contractors
              </Button>
            </Link>
            <RecordActions
              pending={setStatus.isPending}
              onSelect={() =>
                setStatus.mutate({
                  id: supplier.id,
                  status: supplier.status === 'active' ? 'archived' : 'active',
                })
              }
              label={supplier.status === 'active' ? 'Archive' : 'Restore'}
            />
            {form.justSaved ? <SavedNote /> : null}
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </>
        }
      />

      <RecordIdentity
        className="mb-4"
        title={supplier.name}
        symbol={<Icon name="tools" className="size-6" />}
        status={
          <Chip tone={supplier.status === 'archived' ? 'neutral' : 'positive'}>
            {supplier.status === 'archived' ? 'Archived' : 'Active'}
          </Chip>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {SUPPLIER_TRADE_LABELS[supplier.trade]}
            <Ref entity="supplier" sequence={supplier.refSeq} />
          </span>
        }
        facts={[
          {
            label: 'Registration',
            value: supplier.registrationNumber ?? (
              <span className={requiresRegistration(supplier.trade) ? 'text-warn' : ''}>
                {requiresRegistration(supplier.trade) ? 'Required · not recorded' : 'Not recorded'}
              </span>
            ),
          },
          { label: 'GST number', value: supplier.gstNumber ?? 'Not recorded' },
        ]}
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          {editing ? (
            <div className="contents" onChange={form.touch}>
              <EditPanel
                supplier={supplier}
                onDone={() => {
                  form.reset();
                  setEditing(false);
                }}
                onSaved={() => {
                  form.saved();
                  refresh();
                }}
              />
            </div>
          ) : null}

          {/* ── The humans ──────────────────────────────────────────────────── */}
          <Panel className="p-0">
            <PanelHeader
              title="Contacts"
              subtitle="Job updates and payment enquiries"
              className="px-5 pt-5 pb-2"
              aside={
                <Button size="sm" variant="secondary" onClick={() => setAttaching(true)}>
                  Attach a person
                </Button>
              }
            />

            {supplier.contacts.length === 0 ? (
              <p className="border-t border-rule px-5 py-4 text-[0.8125rem] text-ink-muted">
                <strong className="block font-semibold text-ink">No contacts yet</strong>
                Attach a person for job updates and payment enquiries.
              </p>
            ) : (
              <ul className="flex flex-col">
                {supplier.contacts.map((contact) => (
                  // One human can hold two roles on the same company — the office contact who is
                  // also on the tools — so the person alone is not a key.
                  <RelatedRecordRow
                    key={`${contact.personId}-${contact.roleType}`}
                    identity={
                      <Link
                        to="/people/$ref"
                        params={{ ref: formatRef('person', contact.personRefSeq) }}
                      >
                        {displayName(contact)}
                      </Link>
                    }
                    reference={<Ref entity="person" sequence={contact.personRefSeq} />}
                    badges={
                      <>
                        {contact.isPrimary ? <Chip tone="accent">Primary</Chip> : null}
                        <Chip tone="neutral">{ROLE_LABELS[contact.roleType]}</Chip>
                      </>
                    }
                  >
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      {contactMethods(contact).length === 0
                        ? 'No contact details on file'
                        : contactMethods(contact).map((method) => (
                            <a
                              key={method.key}
                              href={method.href}
                              className="min-w-0 break-words rounded-sm py-1 underline-offset-4 hover:text-accent hover:underline"
                            >
                              <span className="mr-2 text-[0.75rem]">{method.label}</span>
                              {method.value}
                            </a>
                          ))}
                    </div>
                  </RelatedRecordRow>
                ))}
              </ul>
            )}
          </Panel>
          {!editing && supplier.notes ? (
            <Panel className="p-5">
              <PanelHeader title="Notes" className="p-0 pb-3" />
              <p className="text-[0.8125rem] whitespace-pre-line text-ink-muted">
                {supplier.notes}
              </p>
            </Panel>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <SupplierAccount supplierId={supplier.id} supplierName={supplier.name} />

          <Panel className="p-5">
            <PanelHeader title="Bills and jobs" className="p-0 pb-3" />
            <p className="text-[0.8125rem] text-ink-muted">
              Supplier-specific bills and job history are not available on this record yet.
            </p>
          </Panel>
        </div>
      </div>

      {attaching ? (
        <AttachRoleDialog
          target={{
            mode: 'subject',
            subject: 'supplier',
            subjectId: supplier.id,
            subjectLabel: supplier.name,
            // Null, not a span. A supplier exists until it is archived, which is a status rather
            // than a date — the same answer an owner entity gives.
            span: null,
          }}
          onClose={() => setAttaching(false)}
          onDone={() => {
            setAttaching(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function EditPanel({
  supplier,
  onDone,
  onSaved,
}: {
  supplier: SupplierDetail;
  onDone: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(supplier.name);
  const [trade, setTrade] = useState<SupplierTrade>(supplier.trade);
  const [registrationNumber, setRegistrationNumber] = useState(supplier.registrationNumber ?? '');
  const [gstNumber, setGstNumber] = useState(supplier.gstNumber ?? '');
  const [notes, setNotes] = useState(supplier.notes ?? '');

  const save = useMutation(
    trpc.suppliers.update.mutationOptions({
      meta: { errorTitle: 'Could not save that contractor' },
      onSuccess() {
        onSaved();
        onDone();
      },
    }),
  );

  const ready = name.trim() !== '';

  return (
    <Panel className="p-5">
      <PanelHeader title="Edit contractor" className="p-0 pb-3" />

      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready) return;
          save.mutate({
            id: supplier.id,
            name: name.trim(),
            trade,
            ...(registrationNumber.trim() === ''
              ? {}
              : { registrationNumber: registrationNumber.trim() }),
            ...(gstNumber.trim() === '' ? {} : { gstNumber: gstNumber.trim() }),
            ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
          });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Trade" required>
            {(props) => (
              <Select
                {...props}
                value={trade}
                onChange={(e) => setTrade(e.target.value as SupplierTrade)}
              >
                {SUPPLIER_TRADES.map((value) => (
                  <option key={value} value={value}>
                    {SUPPLIER_TRADE_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Registration number">
            {(props) => (
              <Input
                {...props}
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
              />
            )}
          </Field>
          <Field label="GST number">
            {(props) => (
              <Input
                {...props}
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
                className="tabular-nums"
              />
            )}
          </Field>
        </div>

        <Field label="Notes">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Field>

        {/*
         * NO BANK ACCOUNT FIELD, on this form or any other that an ordinary edit reaches. It is
         * `trust_admin` work behind a re-verified MFA, on the panel to the right — docs/SECURITY.md
         * §5. A field here would let a correction to a phone number redirect money.
         */}

        <div className="flex items-center gap-2">
          <Button type="submit" size="sm" disabled={!ready || save.isPending}>
            {save.isPending ? 'Saving' : 'Save'}
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function NotFound({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Contractor"
      title={title}
      back={{ label: 'Back to contractors', link: { to: '/maintenance/contractors' } }}
    >
      {children}
    </RecordNotFound>
  );
}
